import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../api/axiosConfig";

const clock = (seconds) => {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
};

function TestAttempt() {
  const { courseId, kind, testId } = useParams();
  const navigate = useNavigate();
  const [paper, setPaper] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sent = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/learn/${courseId}/tests/${kind}/${testId}`)
      .then((res) => {
        if (cancelled) return;
        setPaper(res.data);
        setError("");
        const minutes = Number(res.data.test?.duration) || 0;
        setSecondsLeft(minutes > 0 ? minutes * 60 : null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || "Unable to open this test.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [courseId, kind, testId]);

  useEffect(() => {
    if (secondsLeft === null) return undefined;
    if (secondsLeft <= 0) return undefined;
    const timer = setTimeout(() => setSecondsLeft((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const questions = paper?.questions || [];
  const current = questions[index];
  const answeredCount = useMemo(
    () => questions.filter((question) => answers[question.id]).length,
    [questions, answers]
  );

  const submit = async () => {
    if (submitting || sent.current) return;
    sent.current = true;
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/learn/${courseId}/tests/${kind}/${testId}`, {
        answers: Object.entries(answers).map(([questionId, answer]) => ({
          questionId: Number(questionId),
          answer,
        })),
      });
      navigate(`/my-courses/${courseId}/tests/${kind}/${testId}/result`, { replace: true });
    } catch (err) {
      sent.current = false;
      setError(err.response?.data?.message || "Unable to submit this test.");
      setSubmitting(false);
      setConfirming(false);
    }
  };

  useEffect(() => {
    if (secondsLeft === 0) submit();
  }, [secondsLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <p className="text-center py-5">Loading test...</p>;
  if (!paper?.test || !current) {
    return (
      <main className="section-space">
        <div className="container">
          <p>{error || "Test not found."}</p>
          <Link to={`/my-courses/${courseId}`}>Back to course</Link>
        </div>
      </main>
    );
  }

  const choose = (optionId) => {
    setAnswers((currentAnswers) => ({ ...currentAnswers, [current.id]: optionId }));
  };

  return (
    <main className="exam-page section-space">
      <div className="container">
        <p className="my-course-back">
          <Link to={`/my-courses/${courseId}`}>← Back to course</Link>
        </p>
        <header className="exam-top">
          <div>
            <p className="learn-kicker">Attempt</p>
            <h1>{paper.test.title}</h1>
            <p>
              {answeredCount} of {questions.length} answered
            </p>
          </div>
          {secondsLeft !== null ? (
            <p className={`exam-timer${secondsLeft < 60 ? " is-low" : ""}`}>{clock(secondsLeft)}</p>
          ) : null}
          <button type="button" className="btn-view-course" onClick={() => setConfirming(true)}>
            Submit test
          </button>
        </header>

        {error ? <p className="account-error">{error}</p> : null}

        <div className="exam-layout">
          <article className="exam-card">
            <p className="learn-kicker">
              Question {index + 1} of {questions.length}
            </p>
            <div className="exam-question" dangerouslySetInnerHTML={{ __html: current.question }} />
            <div className="exam-options">
              {current.options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`exam-option${answers[current.id] === option.id ? " is-selected" : ""}`}
                  onClick={() => choose(option.id)}
                >
                  <span>{option.id}</span>
                  <span dangerouslySetInnerHTML={{ __html: option.text }} />
                </button>
              ))}
            </div>
            <div className="exam-move">
              <button type="button" disabled={index === 0} onClick={() => setIndex((value) => value - 1)}>
                Previous
              </button>
              <button
                type="button"
                disabled={index === questions.length - 1}
                onClick={() => setIndex((value) => value + 1)}
              >
                Next
              </button>
            </div>
          </article>

          <aside className="exam-palette" aria-label="Questions">
            {questions.map((question, questionIndex) => (
              <button
                key={question.id}
                type="button"
                className={`exam-dot${questionIndex === index ? " is-current" : ""}${
                  answers[question.id] ? " is-answered" : ""
                }`}
                onClick={() => setIndex(questionIndex)}
              >
                {questionIndex + 1}
              </button>
            ))}
          </aside>
        </div>

        {confirming ? (
          <div className="exam-confirm">
            <div>
              <h2>Submit this attempt?</h2>
              <p>
                {questions.length - answeredCount} question
                {questions.length - answeredCount === 1 ? "" : "s"} still unanswered. You can
                re-attempt this test later.
              </p>
              <div className="test-actions">
                <button type="button" className="btn-view-course" disabled={submitting} onClick={submit}>
                  {submitting ? "Submitting..." : "Submit"}
                </button>
                <button type="button" className="test-action-secondary" onClick={() => setConfirming(false)}>
                  Keep going
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </main>
  );
}

export default TestAttempt;
